// Global Instructions Rule Applied!
//
// The Deal Pipeline → Meeting Notes tab (design §8.E; owner 2026-08-27: minutes,
// themes and transcript all team-visible). Layout (owner 2026-09-15): the
// selected meeting's minutes / follow-ups / notes / transcript, full width,
// with the meeting picked from a dropdown in that section's header (newest
// by default — the registry table is gone), then the investment-themes doc
// beneath. The next-meeting prep doc and the series-wide open follow-ups
// section were retired 2026-09-15 (owner): follow-ups live only beneath the
// meeting that agreed them. People edit the minutes and the follow-ups from
// here (the Edit / Add follow-up modals are hosted in this tab).
//
// Every mutation applies optimistically then refetches (the
// AgentSignalsSection pattern).
//
// Kept-mounted surface: useRefreshOnActivate revalidates when attention
// returns (the app shell hides tabs with display:none, never unmounts).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Col, Modal, Row } from 'antd'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { InputManager } from '../../../core/components/InputManager/InputManager.tsx'
import { FieldTypeEnum, type FieldConfig, type FieldValue } from '../../../core/components/InputManager/types.ts'
import { useRefreshOnActivate } from '../../../core/hooks/useRefreshOnActivate.ts'
import { showErrorToast } from '../../../core/utils/errorToast.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { TaskForm, TaskFormRef, type TaskFormValues } from '../../tasks/forms/TaskForm.tsx'
import {
  fetchPipelineMeetings,
  fetchPipelineMeetingDetail,
  fetchPipelineMeetingThemes,
  fetchPipelineMeetingFollowUps,
  setPipelineMeetingFollowUpStatus,
  createTaskFromFollowUp,
  addPipelineMeetingFollowUp,
  editPipelineMeetingFollowUp,
  removePipelineMeetingFollowUp,
  editPipelineMeetingMinutes,
  restorePipelineMeetingMinutes,
  fetchPipelineMeetingMinutesHistory
} from '../services/pipelineMeetingsService.ts'
import {
  PipelineMeetingListItem,
  PipelineMeetingDetail,
  PipelineMeetingThemes,
  PipelineMeetingFollowUp
} from '../../../state_manager/data_models/pipeline_meetings_model.ts'
import { MeetingDocCard } from './MeetingDocCard.tsx'
import { MeetingMinutesDoc } from './MeetingMinutesDoc.tsx'
import { FollowUpStatus } from './FollowUpTable.tsx'

type DealPick = { value: string; label: string } | null
type ContactPick = { value: string; label: string; email?: string } | null

/** The DEAL / CONTACT inputs report an id string or an option object — normalise both. */
const toDealPick = (value: FieldValue, fallbackLabel: string): DealPick => {
  if (Array.isArray(value)) return toDealPick((value[0] ?? null) as FieldValue, fallbackLabel)
  if (value && typeof value === 'object' && 'value' in (value as Record<string, unknown>)) {
    const option = value as { value?: unknown; label?: unknown }
    return option.value ? { value: String(option.value), label: String(option.label || fallbackLabel) } : null
  }
  return typeof value === 'string' && value ? { value, label: fallbackLabel } : null
}

/** The Edit / Add follow-up modal's working copy. */
interface FollowUpDraft {
  /** The row being edited; null = adding a new follow-up. */
  row: PipelineMeetingFollowUp | null
  deal: DealPick
  description: string
  owner: ContactPick
  dueDate: string | null
}

export default function PipelineMeetingsTab() {
  const [themes, setThemes] = useState<PipelineMeetingThemes | null>(null)
  const [themesLoading, setThemesLoading] = useState(true)
  const [meetings, setMeetings] = useState<PipelineMeetingListItem[]>([])
  const [meetingsLoading, setMeetingsLoading] = useState(true)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [detail, setDetail] = useState<PipelineMeetingDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [followUps, setFollowUps] = useState<PipelineMeetingFollowUp[]>([])
  const [followUpsLoading, setFollowUpsLoading] = useState(false)
  const [taskFor, setTaskFor] = useState<PipelineMeetingFollowUp | null>(null)
  const [taskDeal, setTaskDeal] = useState<DealPick>(null)
  const [creatingTask, setCreatingTask] = useState(false)
  const [followUpDraft, setFollowUpDraft] = useState<FollowUpDraft | null>(null)
  const [savingFollowUp, setSavingFollowUp] = useState(false)
  const taskFormRef = useRef<TaskFormRef>(null)
  // The meeting whose follow-ups are on screen — a slow response for a
  // previously-selected meeting must not overwrite the current one.
  const selectedIdRef = useRef<string | null>(null)
  // Until the user picks a meeting, the selection follows the newest one
  // (owner: default to the latest meeting, always) — a background refetch
  // that brings a newly ingested meeting surfaces it on its own.
  const userPickedRef = useRef(false)
  const token = () => getAccessToken(store.getState().auth)

  const loadThemes = useCallback(async () => {
    setThemesLoading(true)
    try {
      setThemes(await fetchPipelineMeetingThemes(token()))
    } catch (error) {
      console.error('[pipeline-meetings] themes load failed:', error)
      setThemes(null)
    } finally {
      setThemesLoading(false)
    }
  }, [])

  const loadMeetings = useCallback(async () => {
    setMeetingsLoading(true)
    try {
      const list = await fetchPipelineMeetings({ page: 1, page_size: 50 }, token())
      setMeetings(list.data)
      // The user's pick stays while it survives; otherwise the newest.
      setSelectedId((current) => {
        if (userPickedRef.current && current && list.data.some((m) => m.pipeline_meetings__id === current)) {
          return current
        }
        return list.data[0]?.pipeline_meetings__id ?? null
      })
    } catch (error) {
      console.error('[pipeline-meetings] list load failed:', error)
      setMeetings([])
    } finally {
      setMeetingsLoading(false)
    }
  }, [])

  const loadFollowUps = useCallback(async (meetingId: string | null) => {
    if (!meetingId) {
      setFollowUps([])
      return
    }
    setFollowUpsLoading(true)
    try {
      const list = await fetchPipelineMeetingFollowUps(meetingId, token())
      if (selectedIdRef.current === meetingId) setFollowUps(list.data)
    } catch (error) {
      // Keep what is on screen: a transient read failure must not blank the
      // table between two successful refetches.
      console.error('[pipeline-meetings] follow-ups load failed:', error)
    } finally {
      if (selectedIdRef.current === meetingId) setFollowUpsLoading(false)
    }
  }, [])

  const loadDetail = useCallback(async (meetingId: string | null) => {
    if (!meetingId) {
      setDetail(null)
      return
    }
    setDetailLoading(true)
    try {
      setDetail(await fetchPipelineMeetingDetail(meetingId, token()))
    } catch (error) {
      console.error('[pipeline-meetings] detail load failed:', error)
      setDetail(null)
    } finally {
      setDetailLoading(false)
    }
  }, [])

  const loadAll = useCallback(async () => {
    await Promise.all([loadThemes(), loadMeetings(), loadFollowUps(selectedIdRef.current)])
  }, [loadThemes, loadMeetings, loadFollowUps])

  useEffect(() => {
    loadAll()
  }, [loadAll])

  useEffect(() => {
    selectedIdRef.current = selectedId
    setFollowUps([])
    loadDetail(selectedId)
    loadFollowUps(selectedId)
  }, [selectedId, loadDetail, loadFollowUps])

  const activateRef = useRefreshOnActivate(loadAll)

  const selectMeeting = useCallback((meetingId: string) => {
    userPickedRef.current = true
    setSelectedId(meetingId)
  }, [])

  // ── Minutes edits ────────────────────────────────────────────────────────
  const handleSaveMinutes = useCallback(
    async (content: string) => {
      const meetingId = selectedIdRef.current
      if (!meetingId) return
      try {
        await editPipelineMeetingMinutes(meetingId, content, token())
        toast.success('Minutes saved')
      } catch (error) {
        showErrorToast(error instanceof Error ? error.message : 'Failed to save the minutes')
        throw error
      }
      await loadDetail(meetingId)
    },
    [loadDetail]
  )

  const handleRestoreMinutes = useCallback(
    async (docId: string) => {
      const meetingId = selectedIdRef.current
      if (!meetingId) return
      try {
        await restorePipelineMeetingMinutes(meetingId, docId, token())
        toast.success('Minutes restored')
      } catch (error) {
        showErrorToast(error instanceof Error ? error.message : 'Failed to restore the minutes')
        throw error
      }
      await loadDetail(meetingId)
    },
    [loadDetail]
  )

  const handleLoadHistory = useCallback(async (meetingId: string) => {
    try {
      return (await fetchPipelineMeetingMinutesHistory(meetingId, token())).data
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to load the minutes history')
      return []
    }
  }, [])

  // Optimistic: flip the local row first, then confirm against the server
  // (the refetch restores truth either way).
  const handleSetFollowUpStatus = useCallback(
    async (row: PipelineMeetingFollowUp, status: FollowUpStatus) => {
      const id = row.pipeline_meeting_follow_ups__id
      const meetingId = row.pipeline_meeting_follow_ups__meeting_id
      setFollowUps((prev) =>
        prev.map((f) =>
          f.pipeline_meeting_follow_ups__id === id
            ? { ...f, pipeline_meeting_follow_ups__status: status, prog__effective_status: status }
            : f
        )
      )
      try {
        await setPipelineMeetingFollowUpStatus(id, status, token())
      } catch (error) {
        showErrorToast(error instanceof Error ? error.message : 'Failed to update the follow-up')
      }
      await loadFollowUps(meetingId)
    },
    [loadFollowUps]
  )

  const openCreateTask = useCallback((row: PipelineMeetingFollowUp) => {
    setTaskDeal({
      value: row.pipeline_meeting_follow_ups__deal_id,
      label: row.deals__name || 'Deal'
    })
    setTaskFor(row)
  }, [])

  // ── Follow-up edits (owner 2026-09-15) ──────────────────────────────────
  const openEditFollowUp = useCallback((row: PipelineMeetingFollowUp) => {
    setFollowUpDraft({
      row,
      deal: { value: row.pipeline_meeting_follow_ups__deal_id, label: row.deals__name || 'Deal' },
      description: row.pipeline_meeting_follow_ups__description,
      owner: row.owner_contact
        ? {
            value: row.owner_contact.contacts__id,
            label: row.owner_contact.prog__display_name || row.owner_contact.contacts__email || 'Contact',
            email: row.owner_contact.contacts__email || undefined
          }
        : null,
      dueDate: row.pipeline_meeting_follow_ups__due_date
    })
  }, [])

  const openAddFollowUp = useCallback(() => {
    setFollowUpDraft({ row: null, deal: null, description: '', owner: null, dueDate: null })
  }, [])

  const handleFollowUpDraftChange = useCallback((key: string, value: FieldValue) => {
    setFollowUpDraft((draft) => {
      if (!draft) return draft
      switch (key) {
        case 'deal':
          return { ...draft, deal: toDealPick(value, draft.deal?.label || 'Deal') }
        case 'description':
          return { ...draft, description: typeof value === 'string' ? value : '' }
        case 'owner': {
          const pick = toDealPick(value, draft.owner?.label || 'Contact')
          return { ...draft, owner: pick ? { ...pick, email: draft.owner?.email } : null }
        }
        case 'dueDate':
          return { ...draft, dueDate: typeof value === 'string' && value ? value : null }
        default:
          return draft
      }
    })
  }, [])

  const handleFollowUpDraftConfirm = async () => {
    if (!followUpDraft) return
    const meetingId = selectedIdRef.current
    if (!followUpDraft.deal?.value) {
      showErrorToast('Pick the deal this follow-up belongs to')
      return
    }
    if (!followUpDraft.description.trim()) {
      showErrorToast('Describe the follow-up')
      return
    }
    if (!meetingId) return
    setSavingFollowUp(true)
    try {
      const fields = {
        description: followUpDraft.description.trim(),
        owner_contact_id: followUpDraft.owner?.value ?? null,
        due_date: followUpDraft.dueDate,
        deal_id: followUpDraft.deal.value
      }
      if (followUpDraft.row) {
        await editPipelineMeetingFollowUp(followUpDraft.row.pipeline_meeting_follow_ups__id, fields, token())
        toast.success('Follow-up saved')
      } else {
        await addPipelineMeetingFollowUp(meetingId, fields, token())
        toast.success('Follow-up added')
      }
      setFollowUpDraft(null)
      await loadFollowUps(meetingId)
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to save the follow-up')
    } finally {
      setSavingFollowUp(false)
    }
  }

  const handleRemoveFollowUp = useCallback(
    async (row: PipelineMeetingFollowUp) => {
      const id = row.pipeline_meeting_follow_ups__id
      const meetingId = row.pipeline_meeting_follow_ups__meeting_id
      setFollowUps((prev) => prev.filter((f) => f.pipeline_meeting_follow_ups__id !== id))
      try {
        await removePipelineMeetingFollowUp(id, token())
        toast.success('Follow-up removed')
      } catch (error) {
        showErrorToast(error instanceof Error ? error.message : 'Failed to remove the follow-up')
      }
      await loadFollowUps(meetingId)
    },
    [loadFollowUps]
  )

  const followUpFields = useMemo(
    (): FieldConfig[] =>
      followUpDraft
        ? [
            {
              key: 'deal',
              label: 'Deal',
              type: FieldTypeEnum.DEAL,
              value: followUpDraft.deal,
              required: true,
              placeholder: 'Search deals...'
            } as FieldConfig,
            {
              key: 'description',
              label: 'Follow-up',
              type: FieldTypeEnum.TEXTAREA,
              value: followUpDraft.description,
              required: true,
              rows: 3,
              maxLength: 500,
              placeholder: 'What was agreed'
            } as FieldConfig,
            {
              key: 'owner',
              label: 'Owner',
              type: FieldTypeEnum.CONTACT,
              value: followUpDraft.owner,
              required: false,
              mode: 'single',
              allowAddNew: false,
              placeholder: 'Search contacts...'
            } as FieldConfig,
            {
              key: 'dueDate',
              label: 'Due',
              type: FieldTypeEnum.DATE,
              value: followUpDraft.dueDate,
              required: false,
              format: 'YYYY-MM-DD'
            } as FieldConfig
          ]
        : [],
    [followUpDraft]
  )

  // Pre-fill from the follow-up: the description is the one-line what, the
  // resolved owner the default assignee (never auto-assigned — the user
  // reviews the form), the meeting + excerpt the context.
  const taskInitialValues = useMemo(
    (): Partial<TaskFormValues> =>
      taskFor
        ? {
            subject: taskFor.pipeline_meeting_follow_ups__description.slice(0, 80),
            notes: `From Deal Pipeline meeting ${dayjs(taskFor.pipeline_meetings__meeting_date).format('MM/DD/YYYY')} — ${
              taskFor.deals__name || 'deal'
            }${taskFor.pipeline_meeting_follow_ups__source_excerpt ? `: “${taskFor.pipeline_meeting_follow_ups__source_excerpt}”` : ''}`,
            assignedTo: taskFor.owner_contact
              ? [
                  {
                    value: taskFor.owner_contact.contacts__id,
                    label: taskFor.owner_contact.prog__display_name || taskFor.owner_contact.contacts__email || '',
                    email: taskFor.owner_contact.contacts__email
                  }
                ]
              : [],
            dueDate: taskFor.pipeline_meeting_follow_ups__due_date
              ? dayjs(taskFor.pipeline_meeting_follow_ups__due_date)
              : null,
            completed: false
          }
        : {},
    [taskFor]
  )

  const dealFields = useMemo(
    (): FieldConfig[] => [
      {
        key: 'deal',
        label: 'Deal',
        type: FieldTypeEnum.DEAL,
        value: taskDeal,
        required: true,
        placeholder: 'Search deals...'
      } as FieldConfig
    ],
    [taskDeal]
  )

  const handleCreateTaskConfirm = async () => {
    if (!taskFor || !taskFormRef.current) return
    if (!taskDeal?.value) {
      showErrorToast('Pick the deal this task belongs to')
      return
    }
    let taskRecord: unknown
    try {
      // TaskForm owns validation and mints the task id (the Dashboard's
      // add-task flow).
      taskRecord = await taskFormRef.current.validateFields()
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Please check the task details')
      return
    }
    setCreatingTask(true)
    try {
      await createTaskFromFollowUp(taskFor.pipeline_meeting_follow_ups__id, taskRecord, taskDeal.value, token())
      const meetingId = taskFor.pipeline_meeting_follow_ups__meeting_id
      setTaskFor(null)
      toast.success('Task created')
      await loadFollowUps(meetingId)
    } catch (error) {
      showErrorToast(error instanceof Error ? error.message : 'Failed to create task')
    } finally {
      setCreatingTask(false)
    }
  }

  return (
    <div ref={activateRef} className='h-full overflow-y-auto pr-1'>
      <Row gutter={[12, 12]} className='mb-3'>
        <Col xs={24}>
          <MeetingMinutesDoc
            meetings={meetings}
            meetingsLoading={meetingsLoading}
            selectedId={selectedId}
            onSelectMeeting={selectMeeting}
            meeting={detail}
            loading={detailLoading}
            followUps={followUps}
            followUpsLoading={followUpsLoading && followUps.length === 0}
            onSetFollowUpStatus={handleSetFollowUpStatus}
            onCreateTask={openCreateTask}
            onEditFollowUp={openEditFollowUp}
            onAddFollowUp={openAddFollowUp}
            onRemoveFollowUp={handleRemoveFollowUp}
            onSaveMinutes={handleSaveMinutes}
            onRestoreMinutes={handleRestoreMinutes}
            onLoadHistory={handleLoadHistory}
          />
        </Col>
      </Row>
      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <MeetingDocCard
            title='Investment Themes'
            titleColor='purple'
            doc={themes?.themes ?? null}
            loading={themesLoading}
            emptyText='No themes recorded yet — they accumulate as meetings are analyzed.'
          />
        </Col>
      </Row>

      <Modal
        title={followUpDraft?.row ? 'Edit follow-up' : 'Add follow-up'}
        open={followUpDraft != null}
        onOk={handleFollowUpDraftConfirm}
        okText={followUpDraft?.row ? 'Save' : 'Add'}
        confirmLoading={savingFollowUp}
        onCancel={() => setFollowUpDraft(null)}
        destroyOnHidden
        width={560}
      >
        <p className='text-gray-600'>
          {followUpDraft?.row
            ? 'Your change is recorded against you; the original wording stays visible in the minutes.'
            : 'Added to this meeting as agreed by the room; recorded against you.'}
        </p>
        <InputManager
          inputMode='edit'
          fields={followUpFields}
          labelPosition='above'
          onChange={handleFollowUpDraftChange}
        />
      </Modal>

      <Modal
        title='Create task from follow-up'
        open={taskFor != null}
        onOk={handleCreateTaskConfirm}
        okText='Create task'
        confirmLoading={creatingTask}
        onCancel={() => setTaskFor(null)}
        destroyOnHidden
        width={560}
      >
        <p className='text-gray-600'>
          Pre-filled from the follow-up — the assignee defaults to the person the meeting named.
          Edit anything before saving. The task is linked to the deal below and its company, and
          appears in their Tasks lists; completing it completes the follow-up.
        </p>
        <InputManager
          inputMode='edit'
          fields={dealFields}
          labelPosition='above'
          onChange={(_key, value) => setTaskDeal(toDealPick(value, taskDeal?.label || 'Deal'))}
        />
        {/* TaskForm is built for the full-height detail panel (StatesStrip
            precedent): bound the wrapper so the notes editor sizes itself. */}
        <div style={{ height: 430 }}>
          <TaskForm ref={taskFormRef} initialValues={taskInitialValues} />
        </div>
      </Modal>
    </div>
  )
}
